import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { BUDGET_OPTIONS, SERVICE_OPTIONS, TIMELINE_OPTIONS, WHATSAPP_LINK } from "@/data/content";

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  country: "",
  service: "",
  budget: "",
  description: "",
  timeline: "",
  consent: false,
  website: "",
};

const inputClass =
  "bg-[#020617] border-slate-800 text-white text-sm placeholder:text-slate-600 focus-visible:ring-[#38bdf8] focus-visible:border-[#38bdf8] h-11";
const labelClass = "block text-xs font-mono-x uppercase tracking-wider text-slate-400 mb-1.5";

export function QuoteForm({ defaultService = "", formId = "quote" }) {
  const [form, setForm] = useState({ ...initialForm, service: defaultService });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Veuillez indiquer votre nom.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Veuillez indiquer une adresse e-mail valide.";
    if (!form.service) e.service = "Veuillez choisir un service.";
    if (form.description.trim().length < 10) e.description = "Décrivez votre projet en quelques mots (10 caractères minimum).";
    if (!form.consent) e.consent = "Votre consentement est nécessaire pour traiter votre demande.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        company: form.company || null,
        phone: form.phone || null,
        country: form.country || null,
        budget: form.budget || null,
        timeline: form.timeline || null,
      });
      setSent(true);
      toast.success("Demande envoyée", {
        description: "Merci ! Nous revenons vers vous rapidement.",
      });
    } catch (err) {
      toast.error("Envoi impossible", { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid={`${formId}-success`}
        className="bg-[#0f172a] border border-[#38bdf8]/30 rounded-2xl p-8 sm:p-10 text-center"
      >
        <CheckCircle2 className="w-12 h-12 text-[#38bdf8] mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Votre demande est bien envoyée</h3>
        <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
          Merci pour votre confiance. Nous étudions votre projet et revenons vers vous rapidement avec un devis gratuit.
          Pour une réponse encore plus rapide, écrivez-nous directement sur WhatsApp.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <a
            href={`${WHATSAPP_LINK}?text=${encodeURIComponent("Bonjour Koranex, je viens d'envoyer une demande de devis via votre site.")}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${formId}-success-whatsapp`}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#1d4ed8] hover:bg-blue-600 text-white text-xs font-mono-x font-bold uppercase tracking-wider transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            Nous écrire sur WhatsApp
          </a>
          <button
            type="button"
            data-testid={`${formId}-success-new`}
            onClick={() => {
              setSent(false);
              setForm({ ...initialForm, service: defaultService });
            }}
            className="px-6 py-3 rounded-lg bg-[#020617] border border-slate-800 hover:border-[#38bdf8]/50 text-slate-300 text-xs font-mono-x uppercase tracking-wider transition-colors"
          >
            Envoyer une autre demande
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${formId}-form`}
      className="bg-[#0f172a] border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-5"
      noValidate
    >
      {/* Honeypot anti-spam : invisible pour les humains */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={(e) => set("website", e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        style={{ display: "none" }}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${formId}-name`} className={labelClass}>Nom et prénom *</label>
          <Input
            id={`${formId}-name`}
            data-testid={`${formId}-name-input`}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Votre nom"
            className={inputClass}
          />
          {errors.name && <p data-testid={`${formId}-name-error`} className="text-xs text-red-400 mt-1">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor={`${formId}-company`} className={labelClass}>Entreprise (facultatif)</label>
          <Input
            id={`${formId}-company`}
            data-testid={`${formId}-company-input`}
            value={form.company}
            onChange={(e) => set("company", e.target.value)}
            placeholder="Nom de votre structure"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor={`${formId}-email`} className={labelClass}>Adresse e-mail *</label>
          <Input
            id={`${formId}-email`}
            type="email"
            data-testid={`${formId}-email-input`}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="contact@entreprise.com"
            className={inputClass}
          />
          {errors.email && <p data-testid={`${formId}-email-error`} className="text-xs text-red-400 mt-1">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className={labelClass}>Téléphone (facultatif)</label>
          <Input
            id={`${formId}-phone`}
            type="tel"
            data-testid={`${formId}-phone-input`}
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+33 ..."
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`${formId}-country`} className={labelClass}>Pays</label>
          <Input
            id={`${formId}-country`}
            data-testid={`${formId}-country-input`}
            value={form.country}
            onChange={(e) => set("country", e.target.value)}
            placeholder="France, Canada, Belgique..."
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Service recherché *</label>
          <Select value={form.service} onValueChange={(v) => set("service", v)}>
            <SelectTrigger data-testid={`${formId}-service-select`} className={inputClass}>
              <SelectValue placeholder="Choisir un service" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f172a] border-slate-800 text-white">
              {SERVICE_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} data-testid={`${formId}-service-option-${o}`} className="focus:bg-[#1d4ed8] focus:text-white">
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.service && <p data-testid={`${formId}-service-error`} className="text-xs text-red-400 mt-1">{errors.service}</p>}
        </div>
        <div>
          <label className={labelClass}>Budget indicatif (facultatif)</label>
          <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
            <SelectTrigger data-testid={`${formId}-budget-select`} className={inputClass}>
              <SelectValue placeholder="Votre budget" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f172a] border-slate-800 text-white">
              {BUDGET_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-[#1d4ed8] focus:text-white">
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelClass}>Délai souhaité (facultatif)</label>
          <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
            <SelectTrigger data-testid={`${formId}-timeline-select`} className={inputClass}>
              <SelectValue placeholder="Votre échéance" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f172a] border-slate-800 text-white">
              {TIMELINE_OPTIONS.map((o) => (
                <SelectItem key={o} value={o} className="focus:bg-[#1d4ed8] focus:text-white">
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label htmlFor={`${formId}-description`} className={labelClass}>Description du projet *</label>
        <Textarea
          id={`${formId}-description`}
          data-testid={`${formId}-description-input`}
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Présentez votre projet : activité, objectifs, fonctionnalités souhaitées..."
          rows={4}
          className={`${inputClass} h-auto`}
        />
        {errors.description && <p data-testid={`${formId}-description-error`} className="text-xs text-red-400 mt-1">{errors.description}</p>}
      </div>

      <div className="flex items-start gap-3 pt-1">
        <Checkbox
          id={`${formId}-consent`}
          data-testid={`${formId}-consent-checkbox`}
          checked={form.consent}
          onCheckedChange={(v) => set("consent", v === true)}
          className="mt-0.5 border-slate-600 data-[state=checked]:bg-[#1d4ed8] data-[state=checked]:border-[#1d4ed8]"
        />
        <label htmlFor={`${formId}-consent`} className="text-xs text-slate-400 leading-relaxed cursor-pointer">
          J'accepte que mes données soient utilisées par Koranex pour traiter ma demande de devis. *
          Consultez la{" "}
          <Link to="/politique-de-confidentialite" className="text-[#38bdf8] hover:underline" data-testid={`${formId}-privacy-link`}>
            politique de confidentialité
          </Link>
          .
        </label>
      </div>
      {errors.consent && <p data-testid={`${formId}-consent-error`} className="text-xs text-red-400">{errors.consent}</p>}

      <button
        type="submit"
        disabled={submitting}
        data-testid={`${formId}-submit-button`}
        className="w-full py-4 bg-[#1d4ed8] hover:bg-blue-600 disabled:opacity-60 text-white font-mono-x font-bold uppercase text-xs tracking-wider rounded-xl transition-colors shadow-lg shadow-blue-950 kx-glow flex items-center justify-center gap-2"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitting ? "Envoi en cours..." : "Envoyer ma demande de devis gratuit"}
      </button>
    </form>
  );
}
